import React, { useState, useEffect } from 'react';
import { useChat } from '../../../hooks/useChat';
import { Sparkles, RefreshCw, TrendingUp, ChevronDown, ChevronUp, BarChart2 } from 'lucide-react';
import toast from 'react-hot-toast';

const AITreatmentInsights = ({ treatments, metrics }) => {
  const [insights, setInsights] = useState(null);
  const [isExpanded, setIsExpanded] = useState(true);
  const [hasGenerated, setHasGenerated] = useState(false);

  const { response, isLoading, error, sendMessage } = useChat(
    'GEMINI',
    'gemini/gemini-2.5-flash',
    false
  );

  useEffect(() => {
    if (error) toast?.error('AI insights unavailable. Please try again.');
  }, [error]);

  useEffect(() => {
    if (response && !isLoading) {
      try {
        const jsonMatch = response?.match(/```json\n?([\s\S]*?)\n?```/) || response?.match(/(\{[\s\S]*\})/);
        if (jsonMatch) {
          setInsights(JSON.parse(jsonMatch?.[1]));
        } else {
          setInsights({ raw: response });
        }
      } catch {
        setInsights({ raw: response });
      }
    }
  }, [response, isLoading]);

  const generateInsights = () => {
    const totalTreatments = metrics?.totalTreatments || treatments?.length || 0;
    const avgEffectiveness = metrics?.avgEffectiveness || 'N/A';
    const topProduct = metrics?.topProduct || 'N/A';
    const mostTreatedField = metrics?.mostTreatedField || 'N/A';

    const recentTreatments = treatments?.slice(0, 8)?.map(t =>
      `${t?.product || 'Product'} on ${t?.field || 'field'} — effectiveness: ${t?.effectiveness || 'N/A'}%, pest: ${t?.pestType || t?.pest || 'unknown'}`
    )?.join('\n') || 'No treatment data available';

    const messages = [
      {
        role: 'system',
        content: `You are an expert IPM agronomist and data analyst specializing in treatment effectiveness optimization.
Analyze treatment data to identify patterns, inefficiencies, and optimization opportunities.
Always respond with valid JSON in this exact format:
{
  "overallAssessment": "Brief 1-sentence assessment of treatment program performance",
  "performanceScore": 75,
  "insights": [
    {
      "type": "optimization|warning|success|trend",
      "title": "Insight title",
      "detail": "Detailed explanation",
      "impact": "high|medium|low",
      "action": "Recommended action to take"
    }
  ],
  "topRecommendation": "Single most important recommendation",
  "resistanceRisk": "low|medium|high",
  "resistanceNote": "Brief note on resistance management",
  "weatherOptimization": "How to optimize treatments based on seasonal/weather patterns"
}`
      },
      {
        role: 'user',
        content: `Analyze this treatment analytics data and provide optimization insights:

TREATMENT METRICS:
- Total Treatments: ${totalTreatments}
- Average Effectiveness: ${avgEffectiveness}%
- Top Product: ${topProduct}
- Most Treated Field: ${mostTreatedField}

RECENT TREATMENT RECORDS:
${recentTreatments}

Identify patterns, resistance risks, and provide 3-4 actionable optimization insights.`
      }
    ];

    setHasGenerated(true);
    setInsights(null);
    sendMessage(messages, { temperature: 0.4, max_tokens: 900 });
  };

  const getTypeStyle = (type) => {
    switch (type?.toLowerCase()) {
      case 'warning': return 'border-red-200 bg-red-50';
      case 'success': return 'border-green-200 bg-green-50';
      case 'trend': return 'border-blue-200 bg-blue-50';
      default: return 'border-yellow-200 bg-yellow-50';
    }
  };

  const getTypeIcon = (type) => {
    switch (type?.toLowerCase()) {
      case 'warning': return '⚠️';
      case 'success': return '✅';
      case 'trend': return '📈';
      default: return '💡';
    }
  };

  const getImpactBadge = (impact) => {
    switch (impact?.toLowerCase()) {
      case 'high': return 'bg-red-100 text-red-700';
      case 'medium': return 'bg-yellow-100 text-yellow-700';
      default: return 'bg-green-100 text-green-700';
    }
  };

  const getResistanceColor = (risk) => {
    switch (risk?.toLowerCase()) {
      case 'high': return 'text-red-600';
      case 'medium': return 'text-yellow-600';
      default: return 'text-green-600';
    }
  };

  const getScoreColor = (score) => {
    if (score >= 80) return 'text-green-600';
    if (score >= 60) return 'text-yellow-600';
    return 'text-red-600';
  };

  return (
    <div className="bg-white rounded-lg shadow border border-green-100 mb-6">
      {/* Header */}
      <div className="p-4 border-b border-gray-100 flex items-center justify-between">
        <div className="flex items-center space-x-2">
          <div className="p-1.5 bg-green-100 rounded-lg">
            <Sparkles className="w-4 h-4 text-green-600" />
          </div>
          <div>
            <h3 className="text-sm font-semibold text-gray-900">AI Treatment Insights</h3>
            <p className="text-xs text-gray-500">Effectiveness analysis &amp; optimization recommendations</p>
          </div>
        </div>
        <div className="flex items-center space-x-2">
          <button
            onClick={generateInsights}
            disabled={isLoading}
            className="flex items-center space-x-1.5 px-3 py-1.5 bg-green-600 text-white text-xs font-medium rounded-lg hover:bg-green-700 disabled:opacity-60 disabled:cursor-not-allowed transition-colors"
          >
            <RefreshCw className={`w-3 h-3 ${isLoading ? 'animate-spin' : ''}`} />
            <span>{isLoading ? 'Analyzing...' : hasGenerated ? 'Refresh' : 'Analyze'}</span>
          </button>
          <button
            onClick={() => setIsExpanded(!isExpanded)}
            className="p-1 text-gray-400 hover:text-gray-600 transition-colors"
          >
            {isExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
          </button>
        </div>
      </div>
      {isExpanded && (
        <div className="p-4">
          {/* Initial state */}
          {!hasGenerated && !isLoading && (
            <div className="text-center py-6">
              <BarChart2 className="w-10 h-10 text-green-300 mx-auto mb-3" />
              <p className="text-sm text-gray-500 mb-3">
                Analyze your treatment data with AI to uncover effectiveness patterns, resistance risks, and optimization opportunities.
              </p>
              <button
                onClick={generateInsights}
                className="px-4 py-2 bg-green-600 text-white text-sm font-medium rounded-lg hover:bg-green-700 transition-colors"
              >
                Generate Insights
              </button>
            </div>
          )}

          {/* Loading state */}
          {isLoading && (
            <div className="space-y-4 animate-pulse">
              {/* Overall assessment + score skeleton */}
              <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border border-gray-200">
                <div className="flex items-start space-x-2 flex-1">
                  <div className="w-4 h-4 bg-gray-300 rounded flex-shrink-0 mt-0.5"></div>
                  <div className="flex-1 space-y-1.5">
                    <div className="h-3 bg-gray-300 rounded w-4/5"></div>
                    <div className="h-3 bg-gray-200 rounded w-3/5"></div>
                  </div>
                </div>
                <div className="ml-4 flex-shrink-0 text-center">
                  <div className="w-10 h-8 bg-gray-300 rounded mx-auto mb-1"></div>
                  <div className="h-2.5 bg-gray-200 rounded w-8 mx-auto"></div>
                </div>
              </div>

              {/* Insight card skeletons */}
              {[1, 2, 3]?.map(i => (
                <div key={i} className="p-3 rounded-lg border border-gray-200 bg-gray-50 space-y-2">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-1.5">
                      <div className="w-4 h-4 bg-gray-300 rounded"></div>
                      <div className="h-3 bg-gray-300 rounded w-28"></div>
                    </div>
                    <div className="h-5 bg-gray-200 rounded-full w-20"></div>
                  </div>
                  <div className="h-3 bg-gray-200 rounded w-full"></div>
                  <div className="h-3 bg-gray-200 rounded w-5/6"></div>
                  <div className="h-3 bg-gray-100 rounded w-2/3 font-medium"></div>
                </div>
              ))}

              {/* Top recommendation skeleton */}
              <div className="p-3 bg-green-50 rounded-lg border border-green-100 space-y-1.5">
                <div className="h-3 bg-green-200 rounded w-1/3 mb-1"></div>
                <div className="h-3 bg-green-100 rounded w-full"></div>
                <div className="h-3 bg-green-100 rounded w-4/5"></div>
              </div>

              {/* Bottom row: resistance + weather skeletons */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="p-3 bg-orange-50 rounded-lg border border-orange-100 space-y-1.5">
                  <div className="flex items-center space-x-1 mb-1">
                    <div className="h-3 bg-orange-200 rounded w-24"></div>
                    <div className="h-3 bg-orange-300 rounded w-10"></div>
                  </div>
                  <div className="h-3 bg-orange-100 rounded w-full"></div>
                  <div className="h-3 bg-orange-100 rounded w-3/4"></div>
                </div>
                <div className="p-3 bg-blue-50 rounded-lg border border-blue-100 space-y-1.5">
                  <div className="h-3 bg-blue-200 rounded w-32 mb-1"></div>
                  <div className="h-3 bg-blue-100 rounded w-full"></div>
                  <div className="h-3 bg-blue-100 rounded w-4/5"></div>
                </div>
              </div>
            </div>
          )}

          {/* Results */}
          {insights && !isLoading && (
            <div className="space-y-4">
              {/* Overall assessment + score */}
              {insights?.overallAssessment && (
                <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border border-gray-200">
                  <div className="flex items-start space-x-2 flex-1">
                    <TrendingUp className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
                    <p className="text-sm text-gray-700">{insights?.overallAssessment}</p>
                  </div>
                  {insights?.performanceScore && (
                    <div className="ml-4 text-center flex-shrink-0">
                      <div className={`text-2xl font-bold ${getScoreColor(insights?.performanceScore)}`}>
                        {insights?.performanceScore}
                      </div>
                      <div className="text-xs text-gray-500">Score</div>
                    </div>
                  )}
                </div>
              )}

              {/* Insights list */}
              {insights?.insights?.map((insight, idx) => (
                <div key={idx} className={`p-3 rounded-lg border ${getTypeStyle(insight?.type)}`}>
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-xs font-semibold text-gray-800">
                      {getTypeIcon(insight?.type)} {insight?.title}
                    </span>
                    {insight?.impact && (
                      <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${getImpactBadge(insight?.impact)}`}>
                        {insight?.impact} impact
                      </span>
                    )}
                  </div>
                  <p className="text-xs text-gray-700 mb-1">{insight?.detail}</p>
                  {insight?.action && (
                    <p className="text-xs text-gray-600 font-medium">→ {insight?.action}</p>
                  )}
                </div>
              ))}

              {/* Raw response fallback */}
              {insights?.raw && (
                <div className="p-3 bg-gray-50 rounded-lg border border-gray-200">
                  <p className="text-xs text-gray-700 whitespace-pre-wrap">{insights?.raw}</p>
                </div>
              )}

              {/* Top recommendation */}
              {insights?.topRecommendation && (
                <div className="p-3 bg-green-50 rounded-lg border border-green-200">
                  <p className="text-xs font-semibold text-green-800 mb-1">🎯 Top Recommendation</p>
                  <p className="text-xs text-green-700">{insights?.topRecommendation}</p>
                </div>
              )}

              {/* Bottom row: resistance + weather */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {insights?.resistanceNote && (
                  <div className="p-3 bg-orange-50 rounded-lg border border-orange-200">
                    <div className="flex items-center space-x-1 mb-1">
                      <span className="text-xs font-semibold text-orange-800">Resistance Risk:</span>
                      <span className={`text-xs font-bold ${getResistanceColor(insights?.resistanceRisk)}`}>
                        {insights?.resistanceRisk?.toUpperCase()}
                      </span>
                    </div>
                    <p className="text-xs text-orange-700">{insights?.resistanceNote}</p>
                  </div>
                )}
                {insights?.weatherOptimization && (
                  <div className="p-3 bg-blue-50 rounded-lg border border-blue-200">
                    <p className="text-xs font-semibold text-blue-800 mb-1">🌤 Weather Optimization</p>
                    <p className="text-xs text-blue-700">{insights?.weatherOptimization}</p>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default AITreatmentInsights;
